// Page d'inscription au réseau social
import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { User, Mail, Lock, Building2, Eye, EyeOff, ArrowRight, CheckCircle, AtSign, X, Gift } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { toast } from 'sonner';

import { API_URL } from '../../../config/api';

export default function RegisterPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    username: '',      // Pseudo pour le réseau social (si choisi)
    firstName: '',     // Vrai prénom
    lastName: '',      // Vrai nom
    companyName: '',   // Nom de la société (pro uniquement)
    userType: 'particulier',
    displayChoice: 'realname', // 'realname' ou 'pseudo'
    referralCode: ''   // Code parrain
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [showVerificationPopup, setShowVerificationPopup] = useState(false);
  const [referralInfo, setReferralInfo] = useState(null);
  
  // Récupérer le code parrain depuis l'URL
  useEffect(() => {
    const parrain = searchParams.get('parrain');
    if (parrain) {
      setFormData(prev => ({ ...prev, referralCode: parrain.toUpperCase() }));
      // Valider le code
      validateReferralCode(parrain);
    }
  }, [searchParams]);
  
  // Valider le code parrain
  const validateReferralCode = async (code) => {
    if (!code || code.length < 6) return;
    
    try {
      const response = await fetch(`${API_URL}/api/referral/validate/${code.toUpperCase()}`);
      if (response.ok) {
        const data = await response.json();
        setReferralInfo(data);
      } else {
        setReferralInfo(null);
      }
    } catch (err) {
      console.error('Error validating referral code:', err);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.email) {
      newErrors.email = 'Email requis';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email invalide';
    }
    
    // Pseudo toujours requis
    if (!formData.username) {
      newErrors.username = 'Pseudo requis';
    } else if (formData.username.length < 3) {
      newErrors.username = 'Minimum 3 caractères';
    } else if (!/^[a-zA-Z0-9_]+$/.test(formData.username)) {
      newErrors.username = 'Lettres, chiffres et _ uniquement';
    }
    
    if (!formData.firstName) {
      newErrors.firstName = 'Prénom requis';
    }
    
    if (!formData.lastName) {
      newErrors.lastName = 'Nom requis';
    }
    
    if (!formData.password) {
      newErrors.password = 'Mot de passe requis';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Minimum 6 caractères';
    }
    
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Les mots de passe ne correspondent pas';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);
    
    // Use XMLHttpRequest to avoid postMessage/DataCloneError from external scripts
    // Déterminer le display_name selon le choix
    const displayName = formData.displayChoice === 'realname' 
      ? `${formData.firstName} ${formData.lastName}`
      : formData.username;
    
    // Générer un username automatique si pas de pseudo choisi
    const finalUsername = formData.displayChoice === 'pseudo' 
      ? formData.username 
      : `${formData.firstName.toLowerCase()}_${formData.lastName.toLowerCase()}`.replace(/\s/g, '');
    
    const params = new URLSearchParams({
      email: formData.email,
      password: formData.password,
      username: finalUsername,
      first_name: formData.firstName,
      last_name: formData.lastName,
      user_type: formData.userType,
      display_name: displayName,
      display_choice: formData.displayChoice,
      company: formData.companyName || ''
    });
    
    const registerUrl = `${API_URL}/api/ecosystem/auth/register?${params.toString()}`;
    
    const xhr = new XMLHttpRequest();
    xhr.open('POST', registerUrl, true);
    xhr.setRequestHeader('Content-Type', 'application/json');
    
    xhr.onreadystatechange = function() {
      if (xhr.readyState === 4) {
        setLoading(false);
        
        if (xhr.status === 200) {
          try {
            const data = JSON.parse(xhr.responseText);
            // UNIFIÉ: Stocke dans les deux systèmes pour compatibilité
            localStorage.setItem('buyer_token', data.token);
            localStorage.setItem('buyer_user', JSON.stringify(data.user || data.profile));
            localStorage.setItem('ecosystem_token', data.token);
            localStorage.setItem('ecosystem_profile', JSON.stringify(data.profile));
            localStorage.setItem('ecosystem_user', JSON.stringify(data.user || data.profile));
            
            // Appliquer le code parrain si présent
            if (formData.referralCode && data.user?.id) {
              fetch(`${API_URL}/api/referral/apply`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  referral_code: formData.referralCode,
                  new_user_id: data.user.id
                })
              }).then(res => res.json()).then(refData => {
                if (refData.success) {
                  toast.success(`${refData.welcome_bonus} crédits bonus reçus grâce au parrainage !`, {
                    icon: '🎁'
                  });
                }
              }).catch(err => console.log('Referral apply error:', err));
            }
            
            toast.success('Compte créé ! Vérifie ta boîte mail pour confirmer ton email.');
            navigate('/reseau/confirmer-email');
          } catch (parseErr) {
            toast.error('Erreur lors de l\'inscription');
          }
        } else {
          let errorMsg = 'Erreur lors de l\'inscription';
          try {
            const errorData = JSON.parse(xhr.responseText);
            errorMsg = errorData.detail || errorMsg;
          } catch (parseErr) {}
          toast.error(errorMsg);
        }
      }
    };
    
    xhr.onerror = function() {
      setLoading(false);
      toast.error('Erreur de connexion au serveur');
    };
    
    xhr.send();
  };

  return (
    <div className="min-h-screen bg-white flex flex-col items-center py-8 px-4 relative">
      {/* Bouton X - Fermer */}
      <button
        onClick={() => navigate(-1)}
        className="absolute top-4 right-4 z-50 w-10 h-10 flex items-center justify-center rounded-full bg-white/80 backdrop-blur-sm border border-gray-200 shadow-sm hover:bg-gray-100 transition-colors"
        data-testid="register-close-btn"
      >
        <X size={20} className="text-gray-600" />
      </button>
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        {/* Header - Fond blanc */}
        <div className="text-center mb-6">
          <Link to="/abracadaworld" className="inline-block mb-4">
            <img 
              src="/logo-abracadaworld-transparent.png" 
              alt="AbracadaWorld"
              className="h-14 w-auto mx-auto"
            />
          </Link>
          <h1 className="text-[17px] font-bold text-gray-900 mb-1 leading-tight">
            Rejoins une communauté de confiance
          </h1>
          <p className="text-sm text-gray-600">
            Un compte unique pour tous les univers
          </p>
        </div>

        {/* Form - Fond orange doux */}
        <div className="rounded-2xl shadow-xl p-6 border border-orange-200" style={{ backgroundColor: '#FFF7ED' }}>
          <form onSubmit={handleSubmit} className="space-y-4">
            
            {/* Bandeau Compte Certifié */}
            <div className="bg-gradient-to-r from-[#1E3A5F] to-[#2D4A6F] rounded-xl p-3 text-white">
              <div className="flex items-center gap-2 mb-1.5">
                <div className="w-7 h-7 bg-[#E8650A] rounded-lg flex items-center justify-center flex-shrink-0">
                  <CheckCircle size={16} className="text-white" />
                </div>
                <h3 className="font-bold text-sm text-white">Compte certifié</h3>
              </div>
              <p className="text-xs text-white/90 mb-2">
                <strong className="text-white">Chaque personne est vérifiée.</strong> Zéro faux compte.
              </p>
              <div className="flex items-center gap-3 text-xs">
                <div className="flex items-center gap-1 bg-white/15 rounded-full px-2.5 py-0.5 whitespace-nowrap">
                  <span className="font-bold text-white">1 personne = 1 compte</span>
                </div>
                <button 
                  type="button"
                  onClick={() => setShowVerificationPopup(true)}
                  className="text-[#E8650A] underline text-xs hover:text-orange-300 transition-colors whitespace-nowrap"
                >
                  En savoir +
                </button>
              </div>
            </div>

            {/* User Type */}
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setFormData({ ...formData, userType: 'particulier' })}
                className={`p-4 rounded-xl border-2 transition-colors ${
                  formData.userType === 'particulier'
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <User className={`mx-auto mb-2 ${
                  formData.userType === 'particulier' ? 'text-blue-600' : 'text-gray-400'
                }`} size={24} />
                <span className={`text-sm font-medium ${
                  formData.userType === 'particulier' ? 'text-blue-600' : 'text-gray-600'
                }`}>
                  Particulier
                </span>
              </button>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, userType: 'professionnel' })}
                className={`p-4 rounded-xl border-2 transition-colors ${
                  formData.userType === 'professionnel'
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <Building2 className={`mx-auto mb-2 ${
                  formData.userType === 'professionnel' ? 'text-blue-600' : 'text-gray-400'
                }`} size={24} />
                <span className={`text-sm font-medium ${
                  formData.userType === 'professionnel' ? 'text-blue-600' : 'text-gray-600'
                }`}>
                  Professionnel
                </span>
              </button>
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 z-10" size={18} />
                <Input
                  type="email"
                  placeholder="votre@email.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className={`${errors.email ? 'border-red-500' : ''}`}
                  style={{ paddingLeft: '44px' }}
                  data-testid="register-email"
                />
              </div>
              {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
            </div>

            {/* Prénom & Nom - Vrais noms pour les commandes */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Prénom</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 z-10" size={18} />
                  <Input
                    placeholder="Jean"
                    value={formData.firstName}
                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                    className={`${errors.firstName ? 'border-red-500' : ''}`}
                    style={{ paddingLeft: '44px' }}
                    data-testid="register-firstname"
                  />
                </div>
                {errors.firstName && <p className="text-red-500 text-xs mt-1">{errors.firstName}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nom</label>
                <Input
                  placeholder="Dupont"
                  value={formData.lastName}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                  className={`${errors.lastName ? 'border-red-500' : ''}`}
                  data-testid="register-lastname"
                />
                {errors.lastName && <p className="text-red-500 text-xs mt-1">{errors.lastName}</p>}
              </div>
            </div>
            <p className="text-xs text-gray-500 -mt-2">Ton vrai nom est utilisé uniquement pour les commandes et factures.</p>

            {/* Nom de la société - Uniquement pour les professionnels */}
            {formData.userType === 'professionnel' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nom de la société ou entreprise</label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 z-10" size={18} />
                  <Input
                    placeholder="Ma Société SARL"
                    value={formData.companyName}
                    onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                    className={`${errors.companyName ? 'border-red-500' : ''}`}
                    style={{ paddingLeft: '44px' }}
                    data-testid="register-company"
                  />
                </div>
                {errors.companyName && <p className="text-red-500 text-xs mt-1">{errors.companyName}</p>}
              </div>
            )}

            {/* Username / Pseudo - Toujours visible */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Pseudo
              </label>
              <div className="relative">
                <AtSign className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 z-10" size={18} />
                <Input
                  placeholder="ton_pseudo"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') })}
                  className={`${errors.username ? 'border-red-500' : ''}`}
                  style={{ paddingLeft: '44px' }}
                  data-testid="register-username"
                />
              </div>
              {errors.username && <p className="text-red-500 text-xs mt-1">{errors.username}</p>}
            </div>

            {/* Choix d'affichage sur le réseau */}
            <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
              <label className="block text-sm font-semibold text-gray-800 mb-3">
                Comment veux-tu apparaître sur le réseau ?
              </label>
              
              {/* Option 1: Prénom Nom */}
              <label className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer mb-2 transition-all hover:bg-white ${formData.displayChoice === 'realname' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white'}`}>
                <input
                  type="radio"
                  name="displayChoice"
                  value="realname"
                  checked={formData.displayChoice === 'realname'}
                  onChange={(e) => setFormData({ ...formData, displayChoice: e.target.value })}
                  className="mt-1 w-4 h-4 text-blue-600"
                />
                <div className="flex-1">
                  <p className="font-medium text-gray-900">Mon prénom et nom</p>
                  <p className="text-xs text-gray-500">
                    Exemple: <span className="font-medium text-blue-600">{formData.firstName || 'Jean'} {formData.lastName || 'Dupont'}</span>
                  </p>
                  <p className="text-xs text-gray-400 mt-1">Recommandé pour les professionnels et la confiance</p>
                </div>
              </label>
              
              {/* Option 2: Pseudo */}
              <label className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all hover:bg-white ${formData.displayChoice === 'pseudo' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white'}`}>
                <input
                  type="radio"
                  name="displayChoice"
                  value="pseudo"
                  checked={formData.displayChoice === 'pseudo'}
                  onChange={(e) => setFormData({ ...formData, displayChoice: e.target.value })}
                  className="mt-1 w-4 h-4 text-blue-600"
                />
                <div className="flex-1">
                  <p className="font-medium text-gray-900">Mon pseudo</p>
                  <p className="text-xs text-gray-500">
                    Exemple: <span className="font-medium text-blue-600">@{formData.username || 'ton_pseudo'}</span>
                  </p>
                  <p className="text-xs text-gray-400 mt-1">Pour garder ton anonymat sur le réseau</p>
                </div>
              </label>
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Mot de passe</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 z-10" size={18} />
                <Input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className={`${errors.password ? 'border-red-500' : ''}`}
                  style={{ paddingLeft: '44px', paddingRight: '40px' }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password}</p>}
            </div>

            {/* Confirm Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Confirmer le mot de passe</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 z-10" size={18} />
                <Input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  className={`${errors.confirmPassword ? 'border-red-500' : ''}`}
                  style={{ paddingLeft: '44px' }}
                />
              </div>
              {errors.confirmPassword && <p className="text-red-500 text-xs mt-1">{errors.confirmPassword}</p>}
            </div>

            {/* Code Parrain (optionnel) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Code parrain <span className="text-gray-400 font-normal">(optionnel)</span>
              </label>
              <div className="relative">
                <Gift className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <Input
                  placeholder="ABC123XY"
                  value={formData.referralCode}
                  onChange={(e) => {
                    const code = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8);
                    setFormData({ ...formData, referralCode: code });
                    if (code.length >= 6) {
                      validateReferralCode(code);
                    } else {
                      setReferralInfo(null);
                    }
                  }}
                  className="pl-10 uppercase tracking-widest"
                  data-testid="register-referral"
                />
              </div>
              {referralInfo && (
                <div className="mt-2 p-2 bg-emerald-50 border border-emerald-200 rounded-lg flex items-center gap-2">
                  <Gift className="w-4 h-4 text-emerald-600" />
                  <p className="text-xs text-emerald-700">
                    <strong>+{referralInfo.welcome_bonus} crédits</strong> offerts par {referralInfo.sponsor_name} !
                  </p>
                </div>
              )}
            </div>

            {/* Submit */}
            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-blue-600 hover:bg-blue-700"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
              ) : (
                <>
                  Créer mon compte
                  <ArrowRight size={18} className="ml-2" />
                </>
              )}
            </Button>
          </form>

          {/* Footer */}
          <p className="text-center text-sm text-gray-600 mt-6">
            Déjà un compte ?{' '}
            <Link to="/reseau/connexion" className="text-blue-600 hover:underline font-medium">
              Se connecter
            </Link>
          </p>
        </div>

        {/* Pro CTA */}
        {formData.userType === 'professionnel' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="mt-6 p-4 bg-orange-50 rounded-xl border border-orange-200"
          >
            <p className="text-sm text-orange-800">
              <strong>Artisan ?</strong> Après l'inscription, active ton Espace Pro pour accéder au CRM, 
              créer ta boutique et bénéficier d'un assistant dédié.
            </p>
          </motion.div>
        )}
      </motion.div>

      {/* Popup Vérification d'identité */}
      {showVerificationPopup && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
          onClick={() => setShowVerificationPopup(false)}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle size={24} className="text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900">Publiez en toute sécurité</h3>
            </div>
            
            <p className="text-gray-600 mb-4">
              Chez Abracadaworld, nous garantissons un environnement de confiance grâce à notre processus de vérification d'identité.
            </p>
            
            <div className="space-y-3 mb-6">
              <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-blue-600 font-bold text-sm">1</span>
                </div>
                <div>
                  <p className="font-medium text-gray-900">Pièce d'identité</p>
                  <p className="text-sm text-gray-500">Carte d'identité ou passeport valide</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-blue-600 font-bold text-sm">2</span>
                </div>
                <div>
                  <p className="font-medium text-gray-900">Selfie de vérification</p>
                  <p className="text-sm text-gray-500">Pour confirmer que tu es bien le titulaire</p>
                </div>
              </div>
            </div>
            
            <p className="text-xs text-gray-500 mb-4">
              Tes données sont protégées et ne sont jamais partagées. Processus conforme RGPD.
            </p>
            
            <Button 
              onClick={() => setShowVerificationPopup(false)}
              className="w-full h-11 bg-green-600 hover:bg-green-700"
            >
              J'ai compris
            </Button>
          </motion.div>
        </div>
      )}
    </div>
  );
}
